import { excludedKeys, relationFields } from '@skop-app/lib-domain/constants'
import type { InfiniteData } from '@tanstack/react-query'
import type { Product } from '@skop-app/lib-domain/types'

import {
  flattenObjectKeys,
  formatPropertyValues,
  removeEmptyFields,
  resolveLookupLabel,
} from '@/utils/product.handler'
import { matchKey } from '@/utils/matchKey.utils'
import type { AdaptedProduct, Context, OptionLookup } from '@/types'
import { getProductStatusesItems } from './productStatuses.cache'

type QueryPage = {
  items: Product[]
  nextToken: string | null
}

type ProductStatusesInput = InfiniteData<QueryPage, unknown> | Product[]

type ResolveOptionLabelParams = {
  value: string | number | undefined
  options: Parameters<typeof matchKey>[1]
  optionsByID?: Record<string, OptionLookup> | undefined
  refData?: Record<string, string> | undefined
  originalValue?: unknown
  deletedOptionIds?: readonly string[]
}

type ShapeOption = {
  label: string
  value: string
}

type FlattenedPropertyValues = Record<string, unknown>

const hasOwnField = (record: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key)

const getLookupSources = (context: Partial<Context>, key: string) => {
  const contextRecord = context as Record<string, unknown>

  const references =
    key === 'lot'
      ? context?.propertyOptions?.category
      : (context?.propertyOptions?.[key] ??
        (Array.isArray(contextRecord[key])
          ? (contextRecord[key] as Parameters<typeof matchKey>[1])
          : undefined))

  const optionsByID =
    key === 'lot' ? context?.optionsByID?.category : context?.optionsByID?.[key]

  const refData =
    key === 'lot'
      ? context?.refDataByKey?.category
      : context?.refDataByKey?.[key]

  const deletedOptionIds =
    context.deletedOptionIdsByKey?.[key === 'lot' ? 'category' : key]

  return {
    references,
    optionsByID,
    refData,
    deletedOptionIds,
  }
}

const resolveLabelWithOriginalFallback = ({
  value,
  options,
  optionsByID,
  refData,
  originalValue,
  deletedOptionIds,
}: ResolveOptionLabelParams): string => {
  const stringValue = value !== undefined && value !== null ? String(value) : ''
  if (deletedOptionIds?.includes(stringValue)) return ''
  const label = resolveLookupLabel({
    value: stringValue,
    references: options,
    optionsByID,
    refData,
  })
  if (label) return label

  const originalPrimitive =
    originalValue !== undefined &&
    originalValue !== null &&
    typeof originalValue !== 'object'
      ? String(originalValue)
      : undefined

  if (originalPrimitive && originalPrimitive !== stringValue) {
    return originalPrimitive
  }

  const match =
    Array.isArray(options) && options.length > 0
      ? matchKey(stringValue, options, optionsByID)
      : undefined

  const shouldFallbackToOriginal =
    match?.review === true && originalPrimitive !== undefined

  if (shouldFallbackToOriginal) {
    return originalPrimitive
  }

  return match?.label ?? stringValue
}

const toShapeOption = (value: unknown): ShapeOption | undefined => {
  if (typeof value === 'string' || typeof value === 'number') {
    const normalizedValue = String(value)
    return normalizedValue
      ? { label: normalizedValue, value: normalizedValue }
      : undefined
  }

  if (typeof value !== 'object' || value === null) return undefined
  const record = value as Record<string, unknown>

  const label = typeof record.label === 'string' ? record.label : undefined
  const normalizedValue =
    typeof record.value === 'string'
      ? record.value
      : typeof record.value === 'number'
        ? String(record.value)
        : label

  if (!normalizedValue) return undefined
  return {
    label: label ?? normalizedValue,
    value: normalizedValue,
  }
}

const normalizeShapeID = (value: unknown): ShapeOption[] => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => toShapeOption(entry))
      .filter((entry): entry is ShapeOption => entry !== undefined)
  }

  const single = toShapeOption(value)
  return single ? [single] : []
}

export const adaptProductStatus = (
  item: Product | Record<string, unknown>,
  context: Partial<Context>,
  originalItem?: Product | Record<string, unknown>,
  propertyValues: FlattenedPropertyValues = {},
): AdaptedProduct => {
  const itemRecord = item as Record<string, unknown>
  const originalItemRecord = originalItem as Record<string, unknown> | undefined
  const adaptedKeys = new Set([
    ...Object.keys(itemRecord),
    ...Object.keys(propertyValues),
  ])

  return Array.from(adaptedKeys)
    .filter((key) => ![...relationFields, ...excludedKeys].includes(key))
    .reduce<Record<string, unknown>>((acc, key) => {
      const { references, optionsByID, refData, deletedOptionIds } =
        getLookupSources(context, key)
      const hasLookupSource =
        (Array.isArray(references) && references.length > 0) ||
        (optionsByID !== undefined && Object.keys(optionsByID).length > 0) ||
        (refData !== undefined && Object.keys(refData).length > 0) ||
        Boolean(deletedOptionIds?.length)

      const metadata = context.supportedKeys?.find(
        (k) =>
          k.key === key ||
          (k.key === 'provenance' && key === 'provenances') ||
          (k.key === 'condition' && key === 'conditions'),
      )
      const isChannelsField = key === 'channels'
      const usefulMeta = {
        type: isChannelsField ? 'LIST' : metadata?.type || 'FIELD',
        isRef: !!metadata?.isRef,
        isID: !!metadata?.isID,
        isMulti: isChannelsField || !!metadata?.isMulti,
        isLocked: !!metadata?.isLocked,
      }

      const topLevelExists = hasOwnField(itemRecord, key)
      const propertyExists = hasOwnField(propertyValues, key)
      const topLevelValue = topLevelExists ? itemRecord[key] : undefined
      const propertyValue = propertyExists ? propertyValues[key] : undefined
      const hasUsableTopLevelValue =
        topLevelExists && topLevelValue !== null && topLevelValue !== undefined
      const sourceValue =
        usefulMeta.type === 'LIST' && propertyExists
          ? propertyValue
          : hasUsableTopLevelValue
            ? topLevelValue
            : propertyValue
      const rawValue = Array.isArray(sourceValue) ? sourceValue : [sourceValue]
      const value = usefulMeta.isMulti ? rawValue : rawValue?.[0]
      const originalValue = hasUsableTopLevelValue
        ? originalItemRecord?.[key]
        : propertyValue

      if (hasLookupSource) {
        if (usefulMeta.isMulti) {
          const originalArray = Array.isArray(originalValue)
            ? originalValue
            : []

          acc[key] = rawValue.map((val, index) => ({
            label: resolveLabelWithOriginalFallback({
              value: val,
              options: references ?? [],
              optionsByID,
              refData,
              originalValue: originalArray[index],
              deletedOptionIds,
            }),
            value: val,
            ...usefulMeta,
          }))
        } else {
          acc[key] = {
            label: resolveLabelWithOriginalFallback({
              value,
              options: references ?? [],
              optionsByID,
              refData,
              originalValue,
              deletedOptionIds,
            }),
            value,
            ...usefulMeta,
          }
        }
      } else if (
        metadata?.type === 'RATING' ||
        metadata?.type === 'RATING_RELATIVE'
      ) {
        const starsRawValues =
          typeof value === 'string' ? value.split('/') : value
        const nbrOfStars = Number(starsRawValues?.[0]) || 0
        const isRelative = starsRawValues?.[1] === '0'
        const starCharacter = isRelative ? '☆' : '★'
        const stars = nbrOfStars > 0 ? starCharacter.repeat(nbrOfStars) : ''
        acc[key] = { label: stars, value, ...usefulMeta }
      } else if (key === 'pictures' || key === 'proofPictures') {
        acc[key] = Array.isArray(rawValue)
          ? rawValue.filter(
              (entry): entry is string =>
                typeof entry === 'string' && entry.trim().length > 0,
            )
          : []
      } else {
        const originalPrimitive =
          originalValue !== undefined &&
          originalValue !== null &&
          typeof originalValue !== 'object'
            ? String(originalValue)
            : undefined
        acc[key] = { label: value, value, ...usefulMeta }
        if (
          (usefulMeta.type === 'LIST' || usefulMeta.isRef || usefulMeta.isID) &&
          originalPrimitive &&
          originalPrimitive.length > 0
        ) {
          acc[key] = {
            label: originalPrimitive,
            value,
            ...usefulMeta,
          }
        }
      }

      return acc
    }, {}) as AdaptedProduct
}

export const adaptProductStatuses = (
  data: ProductStatusesInput,
  context: Partial<Context>,
): AdaptedProduct[] => {
  const items = getProductStatusesItems(data)
  const groupedItems: Record<string, number> = {}

  items.forEach((item) => {
    if (!groupedItems[item.productID]) {
      groupedItems[item.productID] = 1
    } else {
      groupedItems[item.productID] += 1
    }
  })

  return items.map((line) => {
    const { properties, ...item } = line

    removeEmptyFields(item)

    const updatedProperties = formatPropertyValues(properties)
    const flattenProperties = updatedProperties
      ? updatedProperties.reduce(
          (acc, curr) => ({ ...acc, ...curr }),
          {} as Record<string, unknown>,
        )
      : {}

    if (groupedItems[item.productID] > 1) {
      item.groups = [
        { label: '', type: 'product', value: item.productID },
        { label: '', type: 'occurrence', value: item?.id },
      ]
    }
    if (!item.groups?.length) {
      item.groups = [{ label: '', type: 'product', value: item?.id }]
    }

    const categoryID = flattenProperties?.category?.[0]
    const categoryOptions = context.propertyOptions?.category ?? []
    if (categoryID && categoryOptions.length) {
      const lot = categoryOptions.filter((c) =>
        c.options?.find((o) => o.id === categoryID || o.label === categoryID),
      )?.[0]
      if (lot) {
        item.lot = lot.id
        flattenProperties.lot = lot.id
      }
    }

    const batchedProductIDs = [
      item?.id,
      ...(item?.batchedProducts?.items || [])
        .filter((i) => i?.product?.id !== item?.id)
        .map((i) => i?.product?.id),
    ]

    flattenObjectKeys(item)

    const adapted = adaptProductStatus(item, context, item, flattenProperties)
    const adaptedRecord = adapted as Record<string, unknown>
    const shapeID = normalizeShapeID(adaptedRecord.shapeID)
    const pictures = Array.isArray(item.pictures)
      ? item.pictures.filter(
          (picture): picture is string => typeof picture === 'string',
        )
      : []

    return {
      ...adapted,
      isPromoted: item.isPromoted,
      dimensionRange: item.dimensionRange,
      location: item.location,
      city: item.city,
      id: item?.id,
      productID: item.productID,
      pictures,
      shapeID,
      groups: item.groups,
      batchedProductID: item.batchedProductID,
      batchedProductIDs,
      slug: item.slug,
      action: item.action,
      type: item.type,
      progress: item.progress,
      updatedAt: item.updatedAt,
      createdAt: item.createdAt,
      source: item.source,
      partner: item.partner,
      sortIndex: item.sortIndex,
      updatedBy: item.updatedBy,
    } as AdaptedProduct
  })
}
